import React from 'react';
import AndieGenericToolRender from './AndieGenericToolRender';
import { useTranslation } from 'react-i18next';
import {
    CardContent,
    Stack,
    Skeleton,
    Typography,
    useTheme,
    CardActions,
    Divider,
    Button,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import _ from 'lodash';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionWorkflowAi } from '@/ai/types/action-workflow-ai';
import { copyClipboard } from '@/utils/copy-clipboard';
import { CreateRounded } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';

type ActionResponse = ActionWorkflowAi<{
    copy: string;
    copyVariableUuid?: string | undefined;
}>;

export type AndieBriefingToCopyWorkflowProps = ActionRenderAi<ActionResponse>;

const AndieBriefingToCopyWorkflow = (props: AndieBriefingToCopyWorkflowProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [copied, setCopied] = React.useState(false);
    return (
        <AndieGenericToolRender<ActionResponse>
            title={() => t('andie.tool.briefing-to-copy.title')}
            icon={<CreateRounded fontSize="small" />}
            loadingMessages={() => [t('andie.tool.briefing-to-copy.loading')]}
            completeMessage={() => t('andie.tool.briefing-to-copy.complete')}
            additionalComponent={(props) => {
                const isComplete = props.status === ActionResponseStatusAi.COMPLETE;
                const skeletonRows = 5;
                const copyText = props.result?.result?.copy || '';
                const handleCopy = async () => {
                    copyClipboard(
                        copyText,
                        () => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                        },
                        () => {
                            enqueueSnackbar(t('error.something-wrong'));
                        },
                    );
                };
                return (
                    <>
                        <CardContent>
                            {isComplete && props.result?.result ? (
                                <Stack gap={1}>
                                    <Typography whiteSpace={'pre-line'} variant="body1" sx={{ flex: 1 }}>
                                        {copyText}
                                    </Typography>
                                </Stack>
                            ) : (
                                <Stack gap={1}>
                                    {_.times(skeletonRows, (i) => (
                                        <Skeleton
                                            key={i}
                                            variant="rounded"
                                            height={theme.typography.body1.fontSize}
                                            width={
                                                (i + 1) % 5 === 0 || i + 1 === skeletonRows ? '70%' : '100%'
                                            }
                                        />
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                        {isComplete && props.result?.result && (
                            <>
                                <Divider />
                                <CardActions sx={{ justifyContent: 'center' }}>
                                    <Button
                                        variant="text"
                                        aria-label="copy"
                                        color={copied ? 'info' : 'primary'}
                                        onClick={handleCopy}
                                        startIcon={<ContentCopyIcon fontSize="small" />}
                                        size="small"
                                    >
                                        {copied ? t('str.action.copied') : t('str.action.copy')}
                                    </Button>
                                </CardActions>
                            </>
                        )}
                    </>
                );
            }}
            {...props}
        />
    );
};

export default AndieBriefingToCopyWorkflow;
